import { useState } from "react";
import { mediaLabel, type MatchVideo } from "./useMatchMedia";
import "./matchVideos.scss";

/** Which video opens first: the live stream while the match is on, otherwise highlights, otherwise the first one. */
const pickDefault = (videos: MatchVideo[], ended: boolean) =>
  (!ended && videos.find((v) => v.type === "LIVE_STREAM")) || (ended && videos.find((v) => v.type === "HIGHLIGHTS")) || videos[0];

/**
 * The match's YouTube videos: a 16:9 player for the selected one and, when there are more, a strip to switch between
 * them. Sits inside the match pages, whose .box / .box-h styles apply.
 */
const MatchVideos = ({ videos, live, ended }: { videos: MatchVideo[]; live: boolean; ended: boolean }) => {
  const [selId, setSelId] = useState<number | null>(null);
  if (!videos.length) return null;
  const sel = videos.find((v) => v.id === selId) ?? pickDefault(videos, ended);
  const onAir = live && sel.type === "LIVE_STREAM";
  return (
    <div className="box mvid">
      <div className="box-h">
        <h3>{onAir && <span className="mvid-dot" />}{videos.length > 1 ? "Videos" : mediaLabel(sel.type, ended)}</h3>
        {videos.length > 1 && <span className="sub">{videos.length} videos</span>}
        <a className="mvid-out" href={sel.url} target="_blank" rel="noopener noreferrer">Watch on YouTube ›</a>
      </div>
      {sel.videoId ? (
        <div className="mvid-frame">
          <iframe
            key={sel.id}
            src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(sel.videoId)}?rel=0&playsinline=1`}
            title={sel.title || mediaLabel(sel.type, ended)}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="mvid-none">This video can't play here. <a href={sel.url} target="_blank" rel="noopener noreferrer">Open it on YouTube</a></div>
      )}
      {videos.length > 1 && (
        <div className="mvid-list" role="list">
          {videos.map((v) => (
            <button key={v.id} type="button" role="listitem" className={`mvid-it${v.id === sel.id ? " on" : ""}`} aria-current={v.id === sel.id} onClick={() => setSelId(v.id)}>
              <span className="th">{v.videoId ? <img src={`https://i.ytimg.com/vi/${v.videoId}/mqdefault.jpg`} alt="" loading="lazy" /> : null}</span>
              <span className="tx">
                <small>{mediaLabel(v.type, ended)}</small>
                <b>{v.title || mediaLabel(v.type, ended)}</b>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchVideos;
